<template>
  <div class="search-bar" :class="{ 'search-bar--active': active }">
    <span class="search-bar__search-icon" v-show="!active" @click="onIconClick">
      <search-icon />
    </span>
    <input
      ref="searchInput"
      class="search-bar__input"
      :class="{ 'search-bar__input--active': active }"
      :value="modelValue"
      @input="onInput"
      @focus="onSearchFocus"
      @keyup.enter="onSearch"
      :placeholder="placeholder"
    />
    <span class="search-bar__clear-button" @click="clearSearch" v-show="showClearButton">
      <Union />
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import SearchIcon from '@/assets/SearchIcon.svg'
import Union from '@/assets/Union.svg'
import { debounce } from '@/utils/debounce'

const props = defineProps<{
  modelValue: string
  placeholder: string
  active: boolean
  showClearButton: boolean
}>()

const emits = defineEmits<{
  (event: 'search'): void
  (event: 'focus'): void
  (event: 'emptyClear'): void
  (event: 'update:modelValue', term: string): void
}>()

const searchInput = ref<HTMLInputElement | null>(null)
// Simulate clicking on text input when logo is clicked
const onIconClick = () => {
  if (searchInput.value) {
    searchInput.value.focus()
  }
}

const clearSearch = () => {
  if (searchInput.value) {
    if (props.modelValue !== '') {
      searchInput.value.value = ''
      onInput()
      searchInput.value.focus()
    } else {
      emits('emptyClear')
    }
  }
}

const onSearch = () => {
  emits('search')
}

const debounceUpdate = debounce(onSearch, 300)

const onSearchFocus = () => {
  emits('focus')
}

const onInput = () => {
  if (searchInput.value) {
    emits('update:modelValue', searchInput.value.value)
    debounceUpdate()
  }
}
</script>

<style lang="scss">
.search-bar {
  display: flex;
  align-items: center;
  height: 40px;
  background: $white;
  border-radius: 4px;

  &__search-icon {
    padding-left: 13px;
    margin-bottom: 1px; // To fix the svg export size
    height: 15px;
    cursor: text;

    > svg * {
      stroke: $grey-50;
    }
  }

  &__input {
    background-color: transparent;
    outline: none;
    border: 0;
    border-radius: 4px;
    padding: 11px 13px 11px 7px;
    flex: 1;
    overflow: auto;

    font-family: $font-medium;
    font-size: 14px;
    line-height: 18px;

    &::placeholder {
      color: $grey-50;
    }

    &--active {
      padding-left: 13px;
    }
  }

  &__clear-button {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 8px;
    height: 24px;
    width: 24px;
    border-radius: 50px;
    background-color: $grey-30;
    transition: background-color 150ms $animationCurve;
    cursor: pointer;

    > svg {
      fill: $black;
    }

    &:hover {
      background-color: $grey-10;
    }
  }
}
</style>
